import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { CATEGORIES } from "../translations";
import { SUPPORTED_LANGUAGES, switchLangPath, buildLocalizedPath } from "../i18nConfig";

const SWITCHER_LANGS = SUPPORTED_LANGUAGES.filter((l) => l.indexable);

export default function Navbar() {
  const { t, lang } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [catOpen, setCatOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  function handleLangSwitch(targetLang) {
    setLangOpen(false);
    setMobileOpen(false);
    navigate(switchLangPath(location.pathname, targetLang));
  }

  const activeLang = SWITCHER_LANGS.find((l) => l.code === lang) || SWITCHER_LANGS[0];

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity" data-testid="navbar-logo" aria-label="FileNesto – back to home">
            <img src="/logo-header.webp" alt="FileNesto logo" width="300" height="100" className="h-8 w-auto" fetchpriority="high" />
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to={buildLocalizedPath("", lang) || "/"} className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors" data-testid="navbar-home-link">
              {t.nav.home}
            </Link>

            {/* Categories dropdown */}
            <div className="relative" onMouseEnter={() => setCatOpen(true)} onMouseLeave={() => setCatOpen(false)}>
              <button
                className="flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors"
                data-testid="navbar-categories"
              >
                {t.nav.categories} <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {catOpen && (
                <div className="absolute top-full left-0 pt-2 w-64 z-50">
                  <div className="bg-white rounded-xl shadow-lg border border-slate-200 py-2">
                    {CATEGORIES.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={buildLocalizedPath(cat.slug, lang)}
                        className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors"
                        data-testid={`nav-cat-${cat.slug}`}
                      >
                        <span className="text-sm font-medium text-slate-700">{t.nav[cat.labelKey]}</span>
                        {cat.live ? (
                          <span className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">{t.categories.pdfBadge}</span>
                        ) : (
                          <span className="text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">{t.categories.soonBadge}</span>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/blog" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors" data-testid="navbar-blog">
              Blog
            </Link>
            <Link to="/about" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors" data-testid="navbar-about">
              {t.nav.about}
            </Link>
            <Link to="/contact" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors" data-testid="navbar-contact">
              {t.nav.contact}
            </Link>
          </div>

          <div className="flex items-center gap-3">
            {/* Language switcher dropdown */}
            <div className="relative" onMouseEnter={() => setLangOpen(true)} onMouseLeave={() => setLangOpen(false)}>
              <button
                data-testid="language-switcher"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors text-sm font-medium text-slate-700"
                aria-label="Switch language"
              >
                <span>{activeLang.flag} {activeLang.code.toUpperCase()}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 pt-2 w-36 z-50">
                  <div className="bg-white rounded-xl shadow-lg border border-slate-200 py-1">
                    {SWITCHER_LANGS.map((l) => (
                      <button
                        key={l.code}
                        onClick={() => handleLangSwitch(l.code)}
                        className={`w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-slate-50 transition-colors ${l.code === lang ? "font-semibold text-blue-600" : "text-slate-700"}`}
                        data-testid={`lang-option-${l.code}`}
                      >
                        <span>{l.flag}</span>
                        <span>{l.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button
              className="md:hidden p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden py-3 border-t border-slate-100" data-testid="mobile-menu">
            <Link to={buildLocalizedPath("", lang) || "/"} onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-medium text-slate-700">{t.nav.home}</Link>
            <div className="py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">{t.nav.categories}</div>
            {CATEGORIES.map((cat) => (
              <Link key={cat.slug} to={buildLocalizedPath(cat.slug, lang)} onClick={() => setMobileOpen(false)} className="flex items-center justify-between py-2 text-sm text-slate-700">
                <span>{t.nav[cat.labelKey]}</span>
                {!cat.live && <span className="text-xs text-slate-600">{t.categories.soonBadge}</span>}
              </Link>
            ))}
            <div className="border-t border-slate-100 mt-2 pt-2 space-y-1">
              <Link to="/blog" onClick={() => setMobileOpen(false)} className="block py-2 text-sm text-slate-700">Blog</Link>
              <Link to="/about" onClick={() => setMobileOpen(false)} className="block py-2 text-sm text-slate-700">{t.nav.about}</Link>
              <Link to="/contact" onClick={() => setMobileOpen(false)} className="block py-2 text-sm text-slate-700">{t.nav.contact}</Link>
            </div>
            {/* Mobile language switcher */}
            <div className="border-t border-slate-100 mt-2 pt-2">
              <div className="py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">Language</div>
              <div className="flex flex-wrap gap-2">
                {SWITCHER_LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => handleLangSwitch(l.code)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${l.code === lang ? "border-blue-500 bg-blue-50 text-blue-600" : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"}`}
                    data-testid={`mobile-lang-${l.code}`}
                  >
                    {l.flag} {l.code.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
